import { ArrowUpRight, PenEdit, RecordVideoCamera } from '@griply/assets';
import {
  CalendarEventDetails,
  CalendarEventFormFields,
  CalendarEventResponseStatus,
} from '@griply/types';
import {
  CalendarEventRowState,
  getCalendarEventDescriptionVisible,
  getCalendarEventGuestList,
  getCalendarEventGuestsVisible,
  getCalendarEventRepeatDisplay,
  getCalendarEventWhenLabels,
  getLocationMapsUrl,
} from '@griply/utils';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import {
  CustomFormSelect,
  Size as SelectSize,
} from 'shared/components/ui/custom-form-select';
import { Divider } from 'shared/components/ui/divider';
import { useToday } from 'shared/contexts/today';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { WeekDays } from 'shared/types/week-days';

import { EventFormHeader } from './event-form-header';
import * as Styled from './event-read-only-view.style';
import { EventRow } from './event-row';
import { EventDescriptionRow } from './rows/event-description-row';
import { EventGuestsRow } from './rows/event-guests-row';
import { EventVideoCallRow } from './rows/event-video-call-row';

export type EventReadOnlyViewProps = {
  fields: CalendarEventFormFields;
  timeFormat: TimeFormatOptions;
  weekStartsOn: WeekDays;
  isLoadingDetails?: boolean;
  rowState: CalendarEventRowState;
  // an instance carries no rrule of its own, so the repeat row reads these, not `fields.rrule`
  isRecurring?: boolean;
  seriesRrule?: string | null;
  videoCallUrl?: string | null;
  // composed by getCalendarEventVideoCall: the provider's own conference label, else the enum name
  videoCallLabel: string;
  descriptionHtml?: string | null;
  // Outlook omits the organizer from `guests`, so the display list merges them back in
  organizer?: CalendarEventDetails['organizer'];
  responseStatus?: CalendarEventResponseStatus | null;
  isResponding?: boolean;
  hasRespondError?: boolean;
  // neither provider accepts a counter-proposal through its API, so proposing hands over to theirs
  proposeTimeUrl?: string;
  // responding is a write like any other, so it is gated with editing
  isEditLocked?: boolean;
  onRespond: (status: CalendarEventResponseStatus) => void;
  // absent when the backend sent no provider url, which is what hides the button
  onOpenInCalendar?: () => void;
  onUnlockEdit: () => void;
};

export const EventReadOnlyView: React.FC<EventReadOnlyViewProps> = ({
  fields,
  timeFormat,
  weekStartsOn,
  isLoadingDetails,
  rowState,
  isRecurring,
  seriesRrule,
  videoCallUrl,
  videoCallLabel,
  descriptionHtml,
  organizer,
  responseStatus,
  isResponding,
  hasRespondError,
  proposeTimeUrl,
  isEditLocked,
  onRespond,
  onOpenInCalendar,
  onUnlockEdit,
}) => {
  const { t } = useTranslation();
  const today = useToday();

  const guestList = useMemo(
    () => getCalendarEventGuestList({ guests: fields.guests, organizer }),
    [fields.guests, organizer],
  );

  const { label: repeatLabel } = useMemo(
    () =>
      getCalendarEventRepeatDisplay({
        rrule: fields.rrule,
        seriesRrule,
        isRecurring,
        today,
        t,
        weekStartsOn,
      }),
    [fields.rrule, seriesRrule, isRecurring, today, t, weekStartsOn],
  );

  const { dateLabel, timeLabel } = useMemo(
    () => getCalendarEventWhenLabels(fields, today, timeFormat),
    [fields, today, timeFormat],
  );

  // null for a blank address, which keeps the line plain text instead of a dead link
  const locationMapsUrl = useMemo(
    () => getLocationMapsUrl(fields.location),
    [fields.location],
  );

  const responseOptions = useMemo(
    () => [
      {
        label: t('forms.event.rsvp.options.yes'),
        value: CalendarEventResponseStatus.Accepted,
      },
      {
        label: t('forms.event.rsvp.options.no'),
        value: CalendarEventResponseStatus.Declined,
      },
      {
        label: t('forms.event.rsvp.options.maybe'),
        value: CalendarEventResponseStatus.Tentative,
      },
    ],
    [t],
  );

  const onChangeResponse = (value?: string) =>
    value && onRespond(value as CalendarEventResponseStatus);

  const onProposeNewTime = () =>
    window.open(proposeTimeUrl, '_blank', 'noreferrer');

  const hasGuestRows = getCalendarEventGuestsVisible(
    rowState,
    guestList.length,
  );

  const hasDescriptionRow = getCalendarEventDescriptionVisible(
    rowState,
    fields.description,
  );

  return (
    <Styled.Container>
      <EventFormHeader value={fields.title} disabled />

      <Styled.Summary>
        <Styled.SummaryWhen>
          <Styled.SummaryLine>
            {dateLabel}, {timeLabel ?? t('general.labels.all-day')}
          </Styled.SummaryLine>
          {!!fields.timeZone && (
            <Styled.SummaryLine>{fields.timeZone}</Styled.SummaryLine>
          )}
          {/* deliberately no series-managed fallback here, unlike mobile — an invitee edits nothing */}
          {!!repeatLabel && (
            <Styled.SummaryLine>{repeatLabel}</Styled.SummaryLine>
          )}
        </Styled.SummaryWhen>

        {!!fields.location && (
          <Styled.SummaryLocation>
            {locationMapsUrl ? (
              <Styled.SummaryLocationLink
                href={locationMapsUrl}
                target="_blank"
                rel="noreferrer"
              >
                {fields.location}
              </Styled.SummaryLocationLink>
            ) : (
              fields.location
            )}
          </Styled.SummaryLocation>
        )}
      </Styled.Summary>

      <Divider noMargin />
      <Styled.ScrollArea>
        {!isEditLocked && !!proposeTimeUrl && (
          <Styled.Body>
            <Styled.Section>
              <EventRow>
                <Button
                  variant={Variants.Outlined}
                  size={Sizes.Small}
                  color={Colors.Inherit}
                  endIcon={ArrowUpRight}
                  onClick={onProposeNewTime}
                  type="button"
                >
                  {t('forms.event.propose-time.trigger')}
                </Button>
              </EventRow>
            </Styled.Section>
          </Styled.Body>
        )}

        {hasGuestRows && (
          <>
            <Divider noMargin />
            <Styled.Body>
              <Styled.Section>
                <EventGuestsRow
                  value={guestList}
                  isLoading={rowState.isLoadingGuests}
                  disabled
                />
              </Styled.Section>
            </Styled.Body>
          </>
        )}

        {!!videoCallUrl && (
          <>
            <Divider noMargin />
            <Styled.Body>
              <Styled.Section>
                <EventVideoCallRow
                  url={videoCallUrl}
                  label={videoCallLabel}
                  icon={RecordVideoCamera}
                />
              </Styled.Section>
            </Styled.Body>
          </>
        )}

        {hasDescriptionRow && (
          <>
            <Divider noMargin />
            <Styled.Body>
              <Styled.Section>
                <EventDescriptionRow
                  value={fields.description}
                  html={descriptionHtml}
                  isLoading={rowState.isLoadingDescription}
                  disabled
                />
              </Styled.Section>
            </Styled.Body>
          </>
        )}
      </Styled.ScrollArea>

      <Divider noMargin />

      {isEditLocked ? (
        <Styled.RsvpRow>
          <Button
            variant={Variants.Contained}
            size={Sizes.Small}
            color={Colors.White}
            startIcon={PenEdit}
            onClick={onUnlockEdit}
            type="button"
          >
            {t('general.edit')}
          </Button>
          {!!onOpenInCalendar && (
            <Button
              variant={Variants.Contained}
              size={Sizes.Small}
              color={Colors.White}
              endIcon={ArrowUpRight}
              onClick={onOpenInCalendar}
              type="button"
            >
              {t('forms.event.open-in-calendar')}
            </Button>
          )}
        </Styled.RsvpRow>
      ) : (
        <Styled.RsvpRow>
          <span>{t('forms.event.rsvp.label')}</span>
          <Styled.RsvpControl>
            <CustomFormSelect
              value={responseStatus ?? undefined}
              options={responseOptions}
              onChange={onChangeResponse}
              placeholder={t('forms.event.rsvp.placeholder')}
              size={SelectSize.ExtraSmall}
              disabled={isResponding || isLoadingDetails}
            />
          </Styled.RsvpControl>
        </Styled.RsvpRow>
      )}

      {!isEditLocked && !!hasRespondError && (
        <Styled.ErrorText role="alert">
          {t('forms.event.rsvp.error')}
        </Styled.ErrorText>
      )}
    </Styled.Container>
  );
};
